import { createContext, useContext, useEffect, useState } from "react";

interface AuthUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  profilePictureUrl?: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  /** True once the initial /api/me check has resolved. */
  ready: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetch("/api/me").then(async (res) => {
      if (cancelled) return;
      if (res.ok) {
        const data = await res.json();
        setUser(data.user ?? null);
        setReady(true);
      } else {
        window.location.href = "/login";
      }
    }).catch(() => {
      if (!cancelled) window.location.href = "/login";
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) return null;

  return (
    <AuthContext.Provider value={{ user, ready }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
